import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import {
    Select,
    SelectContent,
    SelectGroup,
    SelectItem,
    SelectLabel,
    SelectTrigger,
    SelectValue,
} from "./ui/select";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { MapPin, PlusCircle } from "lucide-react";
import SuggestLocationDialog from "./SuggestLocationDialog";

const TYPE_LABELS = {
    allocated_bay: "Allocated bays",
    visitor_bay: "Visitor bays",
    communal_road: "Communal roads",
    communal_walk: "Communal walkways",
    communal_carpark: "Communal car parks",
    other: "Other",
};

const TYPE_COLORS = {
    allocated_bay: "#2563EB",     // blue
    visitor_bay: "#D97706",       // amber
    communal_road: "#7C3AED",     // violet
    communal_walk: "#059669",     // emerald
    communal_carpark: "#475569",  // slate
    other: "#94A3B8",
};

/**
 * LocationPicker
 * - Loads all estate locations + estate settings.
 * - Renders an interactive map (when a map document is configured) with clickable pins.
 * - Always renders a grouped dropdown as the canonical control.
 * - Lets residents propose a new location (admin approval flow).
 * - Calls onSelect(loc | null) when the user picks a location.
 * - Calls onMapReady(mapBlobUrl | null) so parent can reuse the same image (e.g. VehiclePlacer).
 */
export default function LocationPicker({ value, onSelect, onMapReady }) {
    const [locations, setLocations] = useState([]);
    const [settings, setSettings] = useState(null);
    const [mapBlobUrl, setMapBlobUrl] = useState(null);
    const [loading, setLoading] = useState(true);
    const [suggestOpen, setSuggestOpen] = useState(false);
    const [pendingSuggestion, setPendingSuggestion] = useState(null);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const [locRes, settRes] = await Promise.all([
                    api.get("/locations"),
                    api.get("/estate/settings"),
                ]);
                if (cancelled) return;
                setLocations(locRes.data || []);
                setSettings(settRes.data);
            } catch {
                // ignore
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    useEffect(() => {
        let revoke;
        let cancelled = false;
        const loadMap = async () => {
            if (!settings?.map_document_id) {
                setMapBlobUrl(null);
                onMapReady?.(null);
                return;
            }
            try {
                const res = await api.get(`/documents/${settings.map_document_id}/download`, { responseType: "blob" });
                if (cancelled) return;
                const url = URL.createObjectURL(res.data);
                revoke = url;
                setMapBlobUrl(url);
                onMapReady?.(url);
            } catch {
                setMapBlobUrl(null);
                onMapReady?.(null);
            }
        };
        loadMap();
        return () => {
            cancelled = true;
            if (revoke) URL.revokeObjectURL(revoke);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [settings?.map_document_id]);

    const grouped = useMemo(() => {
        const out = {};
        for (const l of locations) {
            const t = l.type;
            if (!out[t]) out[t] = [];
            out[t].push(l);
        }
        return out;
    }, [locations]);

    const handleSelect = (id) => {
        const loc = locations.find((l) => l.id === id);
        setPendingSuggestion(null);
        onSelect?.(loc || null, null);
    };

    const handleSuggestionSubmitted = (suggestion) => {
        setPendingSuggestion(suggestion);
        onSelect?.(null, suggestion);
    };

    return (
        <div className="space-y-3" data-testid="location-picker">
            {/* Map view */}
            {mapBlobUrl ? (
                <div className="relative border border-slate-200 rounded-lg bg-white overflow-hidden" data-testid="location-map-container">
                    <img src={mapBlobUrl} alt="Estate layout map" className="w-full h-auto block select-none" draggable={false} />
                    <div className="absolute inset-0">
                        {locations
                            .filter((l) => l.coords_x != null && l.coords_y != null)
                            .map((l) => {
                                const active = value?.id === l.id;
                                return (
                                    <button
                                        type="button"
                                        key={l.id}
                                        title={l.label}
                                        onClick={() => handleSelect(l.id)}
                                        data-testid={`map-pin-${l.code}`}
                                        className="absolute -translate-x-1/2 -translate-y-full flex flex-col items-center"
                                        style={{ left: `${l.coords_x}%`, top: `${l.coords_y}%` }}
                                    >
                                        <span
                                            className={`block transition-transform duration-150 ${active ? "scale-125" : "hover:scale-110"}`}
                                            style={{ color: TYPE_COLORS[l.type] || "#475569" }}
                                        >
                                            <MapPin size={active ? 22 : 18} fill={active ? "currentColor" : "white"} strokeWidth={2.25} />
                                        </span>
                                        {active && (
                                            <span className="text-[10px] font-semibold uppercase tracking-wide bg-white border border-slate-200 rounded px-1 py-0.5 mt-0.5 shadow-sm whitespace-nowrap">
                                                {l.code}
                                            </span>
                                        )}
                                    </button>
                                );
                            })}
                    </div>
                </div>
            ) : (
                <div className="border border-dashed border-slate-200 rounded-lg bg-slate-50 px-4 py-3 text-xs text-slate-500" data-testid="location-map-empty">
                    {loading ? "Loading map…" : "Estate map not yet uploaded. Estate admins can upload one under Admin → Estate map."}
                </div>
            )}

            {/* Dropdown */}
            <div className="space-y-1.5">
                <Label>Location of ticket</Label>
                <Select value={value?.id || ""} onValueChange={handleSelect}>
                    <SelectTrigger data-testid="location-select-trigger">
                        <SelectValue placeholder="Select a parking bay or communal area" />
                    </SelectTrigger>
                    <SelectContent className="max-h-80">
                        {Object.keys(TYPE_LABELS)
                            .filter((t) => grouped[t]?.length)
                            .map((t) => (
                                <SelectGroup key={t}>
                                    <SelectLabel>{TYPE_LABELS[t]}</SelectLabel>
                                    {grouped[t].map((l) => (
                                        <SelectItem key={l.id} value={l.id} data-testid={`location-option-${l.code}`}>
                                            <span className="font-mono text-xs text-slate-500 mr-2">{l.code}</span>
                                            {l.label}
                                        </SelectItem>
                                    ))}
                                </SelectGroup>
                            ))}
                    </SelectContent>
                </Select>
                {value && (
                    <div className="text-xs text-slate-500" data-testid="selected-location-description">
                        {value.description}
                    </div>
                )}
                {pendingSuggestion && !value && (
                    <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800" data-testid="pending-location-suggestion">
                        Pending suggestion: <span className="font-semibold">{pendingSuggestion.label}</span>. You can continue the PCN draft while an estate admin reviews the location.
                    </div>
                )}
            </div>

            <div className="flex items-center justify-between text-xs">
                <span className="text-slate-500">Can&apos;t find your location?</span>
                <Button
                    type="button"
                    variant="outline"
                    onClick={() => setSuggestOpen(true)}
                    className="h-7 text-xs"
                    data-testid="open-suggest-location"
                >
                    <PlusCircle size={12} className="mr-1.5" /> Suggest a new location
                </Button>
            </div>

            <SuggestLocationDialog
                mapBlobUrl={mapBlobUrl}
                open={suggestOpen}
                onOpenChange={setSuggestOpen}
                onSubmitted={handleSuggestionSubmitted}
            />
        </div>
    );
}
